import { Plus } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import {
  useCreateCreatorTag,
  useCreatorTagCounts,
  useCreatorTags,
  useSaveCreatorTagAssignments,
} from "@/api/creator-tags";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { randomTagColor } from "@/lib/creator-tags/colors";
import { error, success, warning } from "@/providers/ToastProvider";

// How many missing-creator labels to spell out in the toast before collapsing
// the rest into "and N more".
const MAX_MISSING_SHOWN = 5;

// Per-tag state across the selection: assigned to all (checked), none
// (unchecked), or some (indeterminate). Indeterminate only ever appears as the
// initial reading — clicking always lands on checked or unchecked.
type TriState = "checked" | "unchecked" | "indeterminate";

const toCheckboxValue = (state: TriState): boolean | "indeterminate" =>
  state === "checked" ? true : state === "unchecked" ? false : "indeterminate";

// indeterminate → checked, checked → unchecked, unchecked → checked
const nextState = (state: TriState): TriState => (state === "checked" ? "unchecked" : "checked");

export type BulkTagCreator = {
  tiktokCreatorOpenId: string | null;
  label?: string;
};

export interface BulkTagModalProps {
  creators: BulkTagCreator[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApplied?: () => void;
}

export const BulkTagModal = ({ creators: selection, open, onOpenChange, onApplied }: BulkTagModalProps) => {
  const openIds = [
    ...new Set(selection.flatMap((creator) => (creator.tiktokCreatorOpenId ? [creator.tiktokCreatorOpenId] : []))),
  ];
  const untaggableLabels = selection.filter((creator) => !creator.tiktokCreatorOpenId).map((creator) => creator.label);
  const labelByOpenId = new Map(selection.map((creator) => [creator.tiktokCreatorOpenId, creator.label]));

  const { data: allTags = [] } = useCreatorTags();
  const { data: counts, isLoading: countsLoading } = useCreatorTagCounts(openIds, open);
  const save = useSaveCreatorTagAssignments();
  const createTag = useCreateCreatorTag();

  const [search, setSearch] = useState("");
  // `baseline` = the state read from the server on open; `staged` = the working
  // set the checkboxes bind to. Save commits the diff of staged vs baseline.
  const [baseline, setBaseline] = useState<Map<string, TriState>>(new Map());
  const [staged, setStaged] = useState<Map<string, TriState>>(new Map());
  const seeded = useRef(false);

  // Seed once per open, once the counts have loaded. Reset everything on close.
  useEffect(() => {
    if (open && counts && !seeded.current) {
      const total = counts.total;
      const seed = new Map<string, TriState>();
      for (const tag of allTags) {
        const assigned = counts.counts[tag.id] ?? 0;
        seed.set(tag.id, assigned === 0 ? "unchecked" : total > 0 && assigned >= total ? "checked" : "indeterminate");
      }
      setBaseline(seed);
      setStaged(new Map(seed));
      seeded.current = true;
    }
    if (!open) {
      seeded.current = false;
      setSearch("");
      setBaseline(new Map());
      setStaged(new Map());
    }
  }, [open, counts, allTags]);

  const toggle = (tagId: string) => {
    setStaged((prev) => {
      const next = new Map(prev);
      next.set(tagId, nextState(prev.get(tagId) ?? "unchecked"));
      return next;
    });
  };

  const query = search.trim();
  const filtered = allTags.filter((tag) => tag.name.toLowerCase().includes(query.toLowerCase()));
  // Offer "Create" only when the query doesn't exactly match an existing tag
  // (case-insensitive); duplicate names are rejected server-side.
  const hasExactMatch = allTags.some((tag) => tag.name.toLowerCase() === query.toLowerCase());
  const showCreate = query.length > 0 && !hasExactMatch;

  const handleCreate = async () => {
    if (!query) return;
    try {
      const tag = await createTag.mutateAsync({ name: query, color: randomTagColor() });
      // New tag: no creator has it yet (baseline unchecked), staged checked so
      // the save assigns it to the whole selection.
      setStaged((prev) => new Map(prev).set(tag.id, "checked"));
      setSearch("");
    } catch {
      // toast handled in the hook
    }
  };

  const handleSave = async () => {
    const add: string[] = [];
    const remove: string[] = [];
    for (const [tagId, state] of staged) {
      const base = baseline.get(tagId) ?? "unchecked";
      if (state === base) continue;
      if (state === "checked") add.push(tagId);
      else if (state === "unchecked") remove.push(tagId);
    }

    if (add.length === 0 && remove.length === 0) {
      onOpenChange(false);
      return;
    }

    try {
      const { creators, missingTiktokCreatorOpenIds } =
        openIds.length > 0
          ? await save.mutateAsync({ tiktokCreatorOpenIds: openIds, add, remove })
          : { creators: [], missingTiktokCreatorOpenIds: [] };
      const missingLabels = [
        ...untaggableLabels,
        ...missingTiktokCreatorOpenIds.map((openId) => labelByOpenId.get(openId)),
      ];

      if (creators.length === 0) {
        // Every selected creator was missing — nothing was applied. Keep the
        // modal open so the user can refine the selection and retry.
        error("Failed to apply all tags", {
          description: "None of the specified creators could be found.",
        });
        return;
      }

      const summary = `Updated tags for ${creators.length} creator${creators.length === 1 ? "" : "s"}`;
      if (missingLabels.length > 0) {
        warning(`${summary} — ${missingLabels.length} could not be found`, {
          description: `Skipped: ${formatMissing(missingLabels)}`,
        });
      } else {
        success(summary);
      }
      onApplied?.();
      onOpenChange(false);
    } catch {
      // A hard failure (e.g. a deleted tag) is surfaced by the hook's error toast.
    }
  };

  // Build a readable "Skipped: …" list, falling back where a label is missing
  // and collapsing the long tail.
  const formatMissing = (missing: (string | undefined)[]) => {
    const labels = missing.map((label) => label ?? "an unknown creator");
    const shown = labels.slice(0, MAX_MISSING_SHOWN);
    const extra = labels.length - shown.length;
    return extra > 0 ? `${shown.join(", ")} and ${extra} more` : shown.join(", ");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col sm:max-w-[32rem]">
        <DialogHeader>
          <DialogTitle>
            Apply tags to {selection.length} selected creator{selection.length === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeader>

        <Command shouldFilter={false} className="rounded-md border border-secondary">
          <CommandInput placeholder="Search or create tags..." value={search} onValueChange={setSearch} />
          <CommandList className="max-h-[50vh]">
            {countsLoading ? (
              <div className="py-8 text-center text-sm text-tertiary">Loading tags...</div>
            ) : (
              <>
                {!showCreate && filtered.length === 0 && <CommandEmpty>No tags found</CommandEmpty>}
                {showCreate && (
                  <CommandGroup>
                    <CommandItem value={`__create__${query}`} onSelect={handleCreate} className="gap-2">
                      <Plus className="size-4 shrink-0" />
                      <span className="truncate">
                        Create <span className="font-medium">“{query}”</span>
                      </span>
                    </CommandItem>
                  </CommandGroup>
                )}
                {filtered.length > 0 && (
                  <CommandGroup>
                    {filtered.map((tag) => {
                      const state = staged.get(tag.id) ?? "unchecked";
                      return (
                        <CommandItem key={tag.id} value={tag.id} onSelect={() => toggle(tag.id)} className="gap-2">
                          <Checkbox checked={toCheckboxValue(state)} className="pointer-events-none" tabIndex={-1} />
                          <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: tag.color }} />
                          <span className="truncate">{tag.name}</span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </>
            )}
          </CommandList>
        </Command>

        <DialogFooter>
          <Button color="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button color="primary" isLoading={save.isPending} isDisabled={countsLoading} onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
